import { useCallback } from "react";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import type {
  BetslipState,
  TokenContext,
} from "@/features/betslip/stores/types";
import { useTokenContext } from "@/features/betslip/hooks/useTokenContext";

/**
 * Caller-injection wrappers around store actions whose last argument is
 * a TokenContext. Each wrapper subscribes to the live context via SWR
 * and threads it through, so consumers call the action with its natural
 * argument list.
 */
const useActionWithTokenContext = <ActionParameters extends readonly unknown[]>(
  selector: (
    state: BetslipState,
  ) => (...parameters: [...ActionParameters, TokenContext]) => void,
): ((...parameters: ActionParameters) => void) => {
  const tokenContext = useTokenContext();
  const action = useBetslipStore(selector);
  return useCallback(
    (...parameters: ActionParameters) => action(...parameters, tokenContext),
    [action, tokenContext],
  );
};

const useToggleMultiLeg = () =>
  useActionWithTokenContext((state) => state.toggleMultiLeg);

const useToggleEventDeactivation = () =>
  useActionWithTokenContext((state) => state.toggleEventDeactivation);

const usePickMultiLegForEvent = () =>
  useActionWithTokenContext((state) => state.pickMultiLegForEvent);

const useRemoveSelection = () =>
  useActionWithTokenContext((state) => state.removeSelection);

const useRemoveSelections = () =>
  useActionWithTokenContext((state) => state.removeSelections);

const useRemoveSemLeg = () =>
  useActionWithTokenContext((state) => state.removeSemLeg);

export {
  useToggleMultiLeg,
  useToggleEventDeactivation,
  usePickMultiLegForEvent,
  useRemoveSelection,
  useRemoveSelections,
  useRemoveSemLeg,
};
